import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { LATER } from '@rundown/domain';
import { EmptyState, useToast } from '../../components';
import { formatHours } from '../../lib/format';
import { useCapability } from '../workspace/useCapability';
import { appendSprint, invalidatePlanning } from '../sprints/api';
import { placeBacklogItem } from '../backlog/api';
import { updateRequest } from '../requests/api';
import { useHolidays } from '../calendar/useHolidays';
import { Column, PlanCard } from './parts';
import { PlanColumn } from './PlanColumn';
import { BACKLOG, type PlanBoardModel, type PlanCardModel } from './usePlanBoard';

/** A long overflow list says nothing past the first screenful. */
const LATER_LIMIT = 25;

/**
 * Layout A — what goes in which sprint.
 *
 * Every OPEN sprint is a column, including the one running. That is the fix:
 * the old Planning screen dropped the running sprint entirely and sent you to
 * another screen to look at it, so "what are we doing now, and what is next"
 * was two destinations. Released sprints stay out — dozens accumulate within
 * months, and what shipped belongs on Completed.
 */
export function PlanningView({
  workspaceId,
  board,
  editing,
  pending,
  setPending,
  selectedId,
  onSelect,
}: {
  readonly workspaceId: string;
  readonly board: PlanBoardModel;
  readonly editing: boolean;
  readonly pending: ReadonlyMap<string, string>;
  readonly setPending: (
    update: (current: ReadonlyMap<string, string>) => ReadonlyMap<string, string>,
  ) => void;
  readonly selectedId: string | null;
  readonly onSelect: (sprintId: string) => void;
}) {
  const canCommit = useCapability('planning.write');
  const canSuggest = useCapability('plan.suggest');
  // Renaming, re-dating and deleting a sprint are corrections, not planning —
  // see SprintsController for why they sit behind a different capability.
  const canAdmin = useCapability('workspace.admin');
  const queryClient = useQueryClient();
  const { say, warn } = useToast();
  const holidays = useHolidays(workspaceId);
  const [activeCard, setActiveCard] = useState<PlanCardModel | null>(null);

  const move = useMutation({
    mutationFn: async ({ card, target }: { card: PlanCardModel; target: string }) => {
      const sprintId = target === BACKLOG ? null : target;
      if (canCommit) return updateRequest(workspaceId, card.requestId, { sprintId });
      return placeBacklogItem(workspaceId, card.requestId, sprintId);
    },
    onMutate: ({ card, target }) => {
      setPending((current) => new Map(current).set(card.requestId, target));
    },
    onSuccess: (_result, { card, target }) => {
      const name = board.columns.find((column) => column.sprint.id === target)?.sprint.name;
      if (canCommit) {
        say(name ? `Committed #${card.number} to ${name}.` : `#${card.number} is back in the backlog.`);
      } else {
        say(
          name
            ? `Suggested #${card.number} for ${name}. It is a suggestion until it is committed; everything else reprojected.`
            : `#${card.number} is back in the backlog; it lands wherever the ranking puts it.`,
        );
      }
    },
    onError: (error) => warn(error.message),
    onSettled: async (_result, _error, { card }) => {
      await invalidatePlanning(queryClient, workspaceId);
      setPending((current) => {
        const next = new Map(current);
        next.delete(card.requestId);
        return next;
      });
    },
  });

  const append = useMutation({
    mutationFn: () => appendSprint(workspaceId),
    onSuccess: (sprint) => say(`Added ${sprint.name}. The backlog reprojected.`),
    onError: (error) => warn(error.message),
    onSettled: () => invalidatePlanning(queryClient, workspaceId),
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const frozenIds = new Set(board.columns.filter((column) => column.frozen).map((c) => c.sprint.id));

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveCard(null);
    if (event.over === null) return;
    const card = event.active.data.current as PlanCardModel | undefined;
    if (card === undefined) return;
    const overId = String(event.over.id);
    const target = overId === LATER ? BACKLOG : overId;

    // A frozen column refuses everything, and says why rather than just
    // snapping the card back with no explanation.
    if (frozenIds.has(target)) {
      warn('That sprint is locked. Moving work in or out of it takes an agreed change of plan.');
      return;
    }

    if (canCommit) {
      if (card.sprintId === target) return;
      if (target === BACKLOG && card.mark !== 'committed') {
        if (card.mark === 'placed')
          say('That is the client’s suggestion. Commit it to a sprint, or leave it for them to move.');
        return;
      }
      move.mutate({ card, target });
      return;
    }

    if (card.mark === 'committed') {
      warn('This one is already committed. Ask in the request if it should move.');
      return;
    }
    if (target === BACKLOG && card.mark !== 'placed') return;
    if (card.mark === 'placed' && card.sprintId === target) return;
    move.mutate({ card, target });
  };

  const { projection } = board;
  if (projection === undefined) {
    return <EmptyState title="Could not load the plan" description={board.error?.message} />;
  }

  const draggable = editing && (canCommit || canSuggest);
  const anyRunning = board.columns.some((column) => column.sprint.state === 'started');

  return (
    <>
      <p className="mb-4 fs-13 text-muted">
        Team capacity {formatHours(projection.capacity.monthlyHours)} a month · ≈{' '}
        {formatHours(projection.capacity.weeklyHours)} a week · {projection.capacity.hotfixReservePct}% held
        for hotfixes → {formatHours(projection.plannableHours)} plannable per sprint
      </p>

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={(event: DragStartEvent) =>
          setActiveCard((event.active.data.current as PlanCardModel | undefined) ?? null)
        }
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveCard(null)}
      >
        <div className="flex items-stretch gap-4 overflow-x-auto pb-2">
          <Column
            id={BACKLOG}
            className="w-[14.375rem] flex-none bg-base"
            bordered
            header={
              <>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-bold">Backlog</span>
                  <span className="fs-11 whitespace-nowrap text-muted">
                    {board.backlog.length} not placed
                  </span>
                </div>
                <p className="mt-0.5 fs-11 text-muted">
                  {draggable
                    ? 'Drag into a sprint to place it. Drop here to send it back.'
                    : 'In rank order.'}
                </p>
              </>
            }
          >
            <div className="flex max-h-[68vh] flex-col gap-2 overflow-y-auto pr-0.5">
              {board.backlog.map((card) => (
                <PlanCard
                  key={card.requestId}
                  card={card}
                  draggable={draggable}
                  pending={pending.has(card.requestId)}
                />
              ))}
            </div>
          </Column>

          {board.columns.length === 0 && (
            <div className="flex w-[13.125rem] flex-none items-center rounded-xl bg-surface-2 p-4 fs-12 text-muted">
              No sprint is open. {canCommit ? 'Add one to start placing work.' : 'One will be added soon.'}
            </div>
          )}

          {board.columns.map((column) => (
            <PlanColumn
              key={column.sprint.id}
              workspaceId={workspaceId}
              column={column}
              today={board.today}
              holidays={holidays.dates}
              holidaysWithin={holidays.within}
              canCommit={canCommit}
              canAdmin={canAdmin}
              draggable={draggable}
              pending={pending}
              anyRunning={anyRunning && column.sprint.state !== 'started'}
              selected={selectedId === column.sprint.id}
              onSelect={() => onSelect(column.sprint.id)}
            />
          ))}

          <Column
            id={LATER}
            className="w-[13.125rem] flex-none bg-surface-2"
            header={
              <>
                <span className="font-bold">Later</span>
                <p className="mt-0.5 fs-11 text-muted">Beyond planned sprints</p>
                <p className="mt-1.5 fs-11 text-muted">≈ {formatHours(board.laterHours)} unscheduled</p>
              </>
            }
          >
            <div className="flex flex-col gap-2">
              {board.later.slice(0, LATER_LIMIT).map((card) => (
                <PlanCard
                  key={card.requestId}
                  card={card}
                  draggable={draggable}
                  pending={pending.has(card.requestId)}
                />
              ))}
              {board.later.length > LATER_LIMIT && (
                <p className="px-1 fs-11 text-muted">
                  and {board.later.length - LATER_LIMIT} more — see the{' '}
                  <Link to="/backlog" className="text-accent">
                    Backlog
                  </Link>
                </p>
              )}
            </div>
          </Column>

          {canCommit && (
            <button
              type="button"
              onClick={() => append.mutate()}
              disabled={append.isPending}
              className="min-h-[26rem] w-[7.5rem] flex-none rounded-xl border border-dashed border-line-stronger fs-12 font-semibold text-muted hover:border-line-heavy hover:text-ink"
            >
              {append.isPending ? 'Adding…' : '+ Add sprint'}
            </button>
          )}
        </div>

        <DragOverlay dropAnimation={null}>
          {activeCard !== null ? (
            <div className="w-[12.5rem] rounded-[9px] border border-line-stronger bg-base px-3 py-2.5 fs-12.5 font-semibold text-ink shadow-lg">
              {activeCard.title}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </>
  );
}
